import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Send, Check, X, Edit2, Save, Loader2,
  Calendar, RefreshCw, Mail, Clock, Zap, Trophy, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface Affirmation {
  id: string;
  week_number: number;
  theme: string;
  emotional_hook: string;
  affirmation_message: string;
  reflection_question: string;
  subject_line: string;
  status: string;
  sent_at: string | null;
  generated_at: string;
}

interface AutomationConfig {
  weekly_affirmations_enabled: boolean;
  send_day_of_week: number;
  send_hour_utc: number;
  last_generation_run_at: string | null;
  last_send_run_at: string | null;
}

interface FlowStats {
  draftReminders: number;
  publishReminders: number;
  postSendFollowups: number;
  milestoneEmails: number;
  reengagementSent: number;
}

const THEME_COLORS: Record<string, string> = {
  'self-worth': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  'family': 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  'friendship': 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  'love-relationships': 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  'gratitude': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  'regret-unsaid-words': 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  'healing-closure': 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
};

const EMAIL_FLOWS = [
  {
    key: 'draft_reminder_email_sent',
    label: 'Draft Reminders',
    desc: 'Nudges users who started but didn\'t finish a message (24h, 3d, 7d)',
    icon: Clock,
    color: 'text-amber-500',
    schedule: 'Daily at 9:00 UTC',
  },
  {
    key: 'publish_reminder_email_sent',
    label: 'Publish Reminders',
    desc: 'Re-engages users who haven\'t published in 10+ days (max 3)',
    icon: Mail,
    color: 'text-blue-500',
    schedule: 'Daily at 11:00 UTC',
  },
  {
    key: 'post_send_followup_sent',
    label: 'Post-Send Follow-ups',
    desc: 'Asks "How did it feel?" 24h after publishing a message',
    icon: Zap,
    color: 'text-emerald-500',
    schedule: 'Every 6 hours',
  },
  {
    key: 'milestone_email_sent',
    label: 'Milestone Emails',
    desc: 'Celebrates reaching 1, 3, 5, and 10 messages sent',
    icon: Trophy,
    color: 'text-purple-500',
    schedule: 'Daily at 10:00 UTC',
  },
  {
    key: 'reengagement',
    label: 'Re-engagement',
    desc: '7-day inactivity check with gentle reminder',
    icon: AlertCircle,
    color: 'text-rose-500',
    schedule: 'Admin-triggered',
  },
];

export default function AdminAffirmations() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [affirmations, setAffirmations] = useState<Affirmation[]>([]);
  const [config, setConfig] = useState<AutomationConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Affirmation>>({});
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [flowStats, setFlowStats] = useState<FlowStats>({
    draftReminders: 0, publishReminders: 0, postSendFollowups: 0,
    milestoneEmails: 0, reengagementSent: 0,
  });

  useEffect(() => {
    if (!user) return;
    supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }).then(({ data }) => {
      setIsAdmin(!!data);
    });
  }, [user?.id]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 3600_000).toISOString();

    const [affRes, configRes, subRes, draftEvt, pubEvt, followEvt, mileEvt, reengEvt] = await Promise.all([
      (supabase as any).from('weekly_affirmations').select('*').order('week_number', { ascending: false }).limit(52),
      (supabase as any).from('email_automation_config').select('*').eq('id', 1).single(),
      (supabase as any).from('affirmation_subscriptions').select('id', { count: 'exact', head: true }).eq('is_active', true),
      (supabase as any).from('events').select('id', { count: 'exact', head: true }).eq('event_name', 'draft_reminder_email_sent').gte('created_at', thirtyDaysAgo),
      (supabase as any).from('events').select('id', { count: 'exact', head: true }).eq('event_name', 'publish_reminder_email_sent').gte('created_at', thirtyDaysAgo),
      (supabase as any).from('events').select('id', { count: 'exact', head: true }).eq('event_name', 'post_send_followup_sent').gte('created_at', thirtyDaysAgo),
      (supabase as any).from('events').select('id', { count: 'exact', head: true }).eq('event_name', 'milestone_email_sent').gte('created_at', thirtyDaysAgo),
      (supabase as any).from('events').select('id', { count: 'exact', head: true }).eq('event_name', 'reengagement_reminder_sent').gte('created_at', thirtyDaysAgo),
    ]);

    setAffirmations(affRes.data ?? []);
    setConfig(configRes.data);
    setSubscriberCount(subRes.count ?? 0);
    setFlowStats({
      draftReminders: draftEvt.count ?? 0,
      publishReminders: pubEvt.count ?? 0,
      postSendFollowups: followEvt.count ?? 0,
      milestoneEmails: mileEvt.count ?? 0,
      reengagementSent: reengEvt.count ?? 0,
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAdmin) loadData();
  }, [isAdmin, loadData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-affirmations', { body: {} });
      if (error) {
        const msg = typeof error === 'object' && 'message' in error ? error.message : String(error);
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      toast.success(data?.message || 'Affirmations generated');
      loadData();
    } catch (err: any) {
      const message = err?.message || 'Generation failed';
      console.error('Generate Buffer error:', err);
      toast.error(message === 'Failed to send a request to the Edge Function'
        ? 'Could not reach the server. Please try again in a moment.'
        : message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-weekly-affirmation', { body: {} });
      if (error) {
        const msg = typeof error === 'object' && 'message' in error ? error.message : String(error);
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      toast.success(data?.message || 'Affirmation sent');
      loadData();
    } catch (err: any) {
      const message = err?.message || 'Send failed';
      console.error('Send Now error:', err);
      toast.error(message === 'Failed to send a request to the Edge Function'
        ? 'Could not reach the server. Please try again in a moment.'
        : message);
    } finally {
      setSending(false);
    }
  };

  const toggleEnabled = async () => {
    if (!config) return;
    const newVal = !config.weekly_affirmations_enabled;
    await (supabase as any).from('email_automation_config')
      .update({ weekly_affirmations_enabled: newVal })
      .eq('id', 1);
    setConfig({ ...config, weekly_affirmations_enabled: newVal });
    toast.success(newVal ? 'Weekly affirmations enabled' : 'Weekly affirmations paused');
  };

  const approveAffirmation = async (id: string) => {
    await (supabase as any).from('weekly_affirmations').update({ status: 'approved' }).eq('id', id);
    toast.success('Approved');
    loadData();
  };

  const rejectAffirmation = async (id: string) => {
    await (supabase as any).from('weekly_affirmations').update({ status: 'rejected' }).eq('id', id);
    toast.success('Rejected — will be regenerated');
    loadData();
  };

  const startEdit = (aff: Affirmation) => {
    setEditingId(aff.id);
    setEditForm({
      subject_line: aff.subject_line,
      emotional_hook: aff.emotional_hook,
      affirmation_message: aff.affirmation_message,
      reflection_question: aff.reflection_question,
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    await (supabase as any).from('weekly_affirmations').update(editForm).eq('id', editingId);
    setEditingId(null);
    toast.success('Saved');
    loadData();
  };

  if (isAdmin === null || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Admin access required.</p>
      </div>
    );
  }

  const draftCount = affirmations.filter(a => a.status === 'draft').length;
  const approvedCount = affirmations.filter(a => a.status === 'approved').length;
  const sentCount = affirmations.filter(a => a.status === 'sent').length;
  const totalBehavioralSent = flowStats.draftReminders + flowStats.publishReminders +
    flowStats.postSendFollowups + flowStats.milestoneEmails + flowStats.reengagementSent;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center gap-4">
          <Link to="/admin" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Admin Hub</span>
          </Link>
          <h1 className="text-lg font-bold text-foreground">Email Automation</h1>
        </div>
      </header>

      <main className="container py-8 max-w-5xl space-y-6">
        <Tabs defaultValue="affirmations">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="affirmations">
              <Sparkles className="h-4 w-4 mr-1.5" />
              Weekly Affirmations
            </TabsTrigger>
            <TabsTrigger value="flows">
              <Zap className="h-4 w-4 mr-1.5" />
              Behavioral Flows
            </TabsTrigger>
          </TabsList>

          {/* ── Weekly Affirmations Tab ── */}
          <TabsContent value="affirmations" className="space-y-6 mt-6">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={config?.weekly_affirmations_enabled ?? false}
                    onCheckedChange={toggleEnabled}
                  />
                  <span className="text-sm font-medium">
                    {config?.weekly_affirmations_enabled ? 'Active' : 'Paused'}
                  </span>
                </div>
                <Badge variant="secondary">{subscriberCount} subscribers</Badge>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleGenerate} disabled={generating}>
                  {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                  Generate Buffer
                </Button>
                <Button size="sm" onClick={handleSendNow} disabled={sending || approvedCount === 0}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Send Next
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-500">{draftCount}</p>
                  <p className="text-xs text-muted-foreground">Drafts (review)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-500">{approvedCount}</p>
                  <p className="text-xs text-muted-foreground">Approved (ready)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{sentCount}</p>
                  <p className="text-xs text-muted-foreground">Sent</p>
                </CardContent>
              </Card>
            </div>

            {/* Schedule Info */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>
                    Sends every <strong>Monday at 14:00 UTC</strong> · 
                    Buffer generates daily at 06:00 UTC · 
                    {config?.last_send_run_at
                      ? ` Last sent: ${new Date(config.last_send_run_at).toLocaleDateString()}`
                      : ' Never sent yet'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Affirmation Cards */}
            <Tabs defaultValue="pending">
              <TabsList>
                <TabsTrigger value="pending">Pending ({draftCount})</TabsTrigger>
                <TabsTrigger value="approved">Approved ({approvedCount})</TabsTrigger>
                <TabsTrigger value="sent">Sent ({sentCount})</TabsTrigger>
              </TabsList>

              <TabsContent value="pending" className="space-y-4 mt-4">
                {draftCount === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No drafts to review. Click "Generate Buffer" to create new affirmations.</p>
                    </CardContent>
                  </Card>
                ) : (
                  affirmations.filter(a => a.status === 'draft').map(aff => (
                    <AffirmationCard
                      key={aff.id} affirmation={aff} isEditing={editingId === aff.id}
                      editForm={editForm} onEditFormChange={setEditForm}
                      onStartEdit={() => startEdit(aff)} onSaveEdit={saveEdit}
                      onCancelEdit={() => setEditingId(null)}
                      onApprove={() => approveAffirmation(aff.id)}
                      onReject={() => rejectAffirmation(aff.id)} showActions
                    />
                  ))
                )}
              </TabsContent>

              <TabsContent value="approved" className="space-y-4 mt-4">
                {affirmations.filter(a => a.status === 'approved').map(aff => (
                  <AffirmationCard
                    key={aff.id} affirmation={aff} isEditing={editingId === aff.id}
                    editForm={editForm} onEditFormChange={setEditForm}
                    onStartEdit={() => startEdit(aff)} onSaveEdit={saveEdit}
                    onCancelEdit={() => setEditingId(null)} showActions={false}
                  />
                ))}
              </TabsContent>

              <TabsContent value="sent" className="space-y-4 mt-4">
                {affirmations.filter(a => a.status === 'sent').map(aff => (
                  <AffirmationCard key={aff.id} affirmation={aff} isEditing={false} showActions={false} />
                ))}
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* ── Behavioral Flows Tab ── */}
          <TabsContent value="flows" className="space-y-6 mt-6">
            {/* Summary */}
            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">Behavioral Emails (30 days)</p>
                  <p className="text-xs text-muted-foreground">Automated triggers across all flows</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">{totalBehavioralSent}</p>
                  <p className="text-xs text-muted-foreground">emails sent</p>
                </div>
              </CardContent>
            </Card>

            {/* Flow Cards */}
            <div className="space-y-4">
              {EMAIL_FLOWS.map((flow) => {
                const count = flow.key === 'draft_reminder_email_sent' ? flowStats.draftReminders
                  : flow.key === 'publish_reminder_email_sent' ? flowStats.publishReminders
                  : flow.key === 'post_send_followup_sent' ? flowStats.postSendFollowups
                  : flow.key === 'milestone_email_sent' ? flowStats.milestoneEmails
                  : flowStats.reengagementSent;

                return (
                  <Card key={flow.key}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg bg-muted ${flow.color}`}>
                          <flow.icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-foreground">{flow.label}</h3>
                            <Badge variant="outline" className="shrink-0">
                              {count} sent
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{flow.desc}</p>
                          <div className="flex items-center gap-1 mt-2">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">{flow.schedule}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Flow Architecture */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Email Flow Architecture</CardTitle>
                <CardDescription>How the automation system works</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-start gap-3">
                  <span className="text-lg">📝</span>
                  <div>
                    <p className="font-medium text-foreground">User starts a draft → 24h/3d/7d reminders</p>
                    <p>Max 3 nudges per user. Spaced at increasing intervals.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">📨</span>
                  <div>
                    <p className="font-medium text-foreground">User publishes a message → Follow-up after 24h</p>
                    <p>"How did it feel?" One-time emotional check-in.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">🏆</span>
                  <div>
                    <p className="font-medium text-foreground">User hits 1/3/5/10 messages → Milestone celebration</p>
                    <p>Unique emotional copy for each milestone. One-time per milestone.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">💤</span>
                  <div>
                    <p className="font-medium text-foreground">10+ days since last publish → Gentle re-engagement</p>
                    <p>Max 3 reminders with 10-day cooldown between each.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">💛</span>
                  <div>
                    <p className="font-medium text-foreground">Weekly → AI-generated affirmation email</p>
                    <p>JacKEY generates rotating themes. Admin reviews before sending.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function AffirmationCard({
  affirmation: aff, isEditing, editForm, onEditFormChange,
  onStartEdit, onSaveEdit, onCancelEdit, onApprove, onReject, showActions = false,
}: {
  affirmation: Affirmation;
  isEditing: boolean;
  editForm?: Partial<Affirmation>;
  onEditFormChange?: (f: Partial<Affirmation>) => void;
  onStartEdit?: () => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  showActions?: boolean;
}) {
  const themeClass = THEME_COLORS[aff.theme] ?? 'bg-muted text-muted-foreground';

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline">Week {aff.week_number}</Badge>
            <span className={`text-xs px-2 py-0.5 rounded-full ${themeClass}`}>
              {aff.theme.replace(/-/g, ' ')}
            </span>
          </div>
          <div className="flex gap-1">
            {!isEditing && onStartEdit && aff.status !== 'sent' && (
              <Button size="sm" variant="ghost" onClick={onStartEdit}>
                <Edit2 className="h-3 w-3" />
              </Button>
            )}
            {showActions && !isEditing && (
              <>
                <Button size="sm" variant="outline" className="text-emerald-600" onClick={onApprove}>
                  <Check className="h-3 w-3 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={onReject}>
                  <X className="h-3 w-3 mr-1" /> Reject
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isEditing && editForm && onEditFormChange ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Subject Line</label>
              <Input value={editForm.subject_line ?? ''} onChange={(e) => onEditFormChange({ ...editForm, subject_line: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Emotional Hook</label>
              <Textarea value={editForm.emotional_hook ?? ''} onChange={(e) => onEditFormChange({ ...editForm, emotional_hook: e.target.value })} rows={2} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Affirmation Message</label>
              <Textarea value={editForm.affirmation_message ?? ''} onChange={(e) => onEditFormChange({ ...editForm, affirmation_message: e.target.value })} rows={3} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Reflection Question</label>
              <Textarea value={editForm.reflection_question ?? ''} onChange={(e) => onEditFormChange({ ...editForm, reflection_question: e.target.value })} rows={2} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={onSaveEdit}><Save className="h-3 w-3 mr-1" /> Save</Button>
              <Button size="sm" variant="ghost" onClick={onCancelEdit}>Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Subject</p>
              <p className="text-sm font-medium">{aff.subject_line}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Hook</p>
              <p className="text-sm italic">{aff.emotional_hook}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Affirmation</p>
              <p className="text-sm font-semibold">{aff.affirmation_message}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Reflection</p>
              <p className="text-sm">{aff.reflection_question}</p>
            </div>
            {aff.sent_at && (
              <p className="text-xs text-muted-foreground">
                Sent: {new Date(aff.sent_at).toLocaleDateString()}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
